import { usePage } from '../../context/PageContext';
import { Form, Spin } from '../Ant';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useEffect, useRef } from 'react';
import { useManageMatterRequestLogic } from '../../hooks/useManageMatterRequestLogic';
import CustomerOptionSelector from './customerOptionSelector';
import NewCustomerForm from './newCustomerForm';
import DetailsForm from './detailsForm';
import CustomModal from '../informationPopup/customModal';
import { MessageQuestion, LinearCloseCirlce, Slash } from '../icons';
import { useTranslation } from 'react-i18next';
import { useManageMatterRequest } from '../../hooks/useManageMatterRequest';
import { useMatterRequestEditPermissions } from '../../hooks/useMatterRequestEditPermissions';
import { useRestrictedUserMatterPermissions } from '../../hooks/useRestrictedUserMatterPermissions';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { FormCard } from '../../assets/styles/components/formCard.styled';

interface ManageMatterRequestProps {
  showHeader?: boolean;
  isFromMailOrAppointment?: boolean;
  mailSubject?: string;
  onMatterRequestCreated?: () => void;
}

const ManageMatterRequest = ({
  showHeader = true,
  isFromMailOrAppointment = false,
  mailSubject = '',
  onMatterRequestCreated,
}: ManageMatterRequestProps) => {
  const { setTitle } = usePage();
  const { t } = useTranslation();
  const {
    isEditing,
    matterRequest,
    updateManageMatterRequest,
    handleBack,
    handleSelectCustomer,
    handleSelectMatterRequestType,
    handleSelectLanguage,
    handleSelectCurrency,
    handleSelectHomeCountry,
    handleSelectCustomerType,
    handleSelectOwner,
    handleSelectResponsible,
    handleSelectBusinessArea,
    handleContinueToDetails,
    getCustomerInputValue,
    handleCustomerOptionChange,
    showDiscardDialog,
    handleDiscardConfirm,
    handleDiscardCancel,
  } = useManageMatterRequestLogic({ isFromMailOrAppointment, mailSubject });

  const {
    onCreateMatterRequest,
    onEditMatterRequest,
    isFormDisabled,
    isNewCustomerFormDisabled,
    isSelectedCustomerInactive,
    isMailAsNoteLoading,
    confirmationForLargeAttachmentsModal,
    showFileSizeWarning,
    handleSwitchMailAsNote,
    handleConfirmLargeAttachments,
    handleCancelLargeAttachments,
    closeFileSizeWarning,
  } = useManageMatterRequest({ onMatterRequestCreated });

  const { isPeopleReadOnly } = useMatterRequestEditPermissions({ isEditing });

  const { goBack } = useNavigationStack();
  const {
    canCreateMatterRequests,
    canEditMatterRequests,
    isLoading: isPermissionLoading,
  } = useRestrictedUserMatterPermissions();
  const isFormAllowed = isEditing ? canEditMatterRequests : canCreateMatterRequests;

  useEffect(() => {
    if (isPermissionLoading || isFormAllowed || isFromMailOrAppointment) return;
    goBack();
  }, [isPermissionLoading, isFormAllowed, isFromMailOrAppointment]);

  const handleBackRef = useRef(handleBack);
  useEffect(() => {
    handleBackRef.current = handleBack;
  }, [handleBack]);

  useEffect(() => {
    if (!showHeader) return;
    setTitle(
      <AddEditTransactionHeader
        handleBack={() => handleBackRef.current()}
        showCross
        title={
          isEditing ? t('matterRequest:editMatterRequest') : t('matterRequest:createMatterRequest')
        }
      />,
    );
  }, [isEditing, showHeader]);

  if (isPermissionLoading) return <Spin fullscreen />;
  if (!isFormAllowed) return null;

  return (
    <div className="h-full w-full overflow-x-hidden pb-gutter add-transaction-form">
      {isMailAsNoteLoading && <Spin fullscreen />}
      <FormCard>
        <Form layout="vertical" className="w-full d-flex fd-column gap-gutter">
          <>
            {!isEditing && (
              <CustomerOptionSelector
                currentStepOption={matterRequest?.currentStepOption}
                onChange={handleCustomerOptionChange}
              />
            )}
            {matterRequest?.currentStep === 'newCustomer' ? (
              <NewCustomerForm
                matterRequest={matterRequest}
                updateManageMatterRequest={updateManageMatterRequest}
                handleSelectCustomerType={handleSelectCustomerType}
                handleSelectHomeCountry={handleSelectHomeCountry}
                handleContinueToDetails={handleContinueToDetails}
                isNewCustomerFormDisabled={isNewCustomerFormDisabled}
              />
            ) : (
              <DetailsForm
                matterRequest={matterRequest}
                updateManageMatterRequest={updateManageMatterRequest}
                handleSelectCustomer={handleSelectCustomer}
                handleSelectMatterRequestType={handleSelectMatterRequestType}
                handleSelectLanguage={handleSelectLanguage}
                handleSelectCurrency={handleSelectCurrency}
                handleSelectOwner={handleSelectOwner}
                handleSelectResponsible={handleSelectResponsible}
                handleSelectBusinessArea={handleSelectBusinessArea}
                onCreateMatterRequest={onCreateMatterRequest}
                onEditMatterRequest={onEditMatterRequest}
                isFormDisabled={isFormDisabled}
                isSelectedCustomerInactive={isSelectedCustomerInactive}
                isEditing={isEditing}
                getCustomerInputValue={getCustomerInputValue}
                isFromMailOrAppointment={matterRequest?.isFromMailOrAppointment}
                handleSwitchMailAsNote={handleSwitchMailAsNote}
                isPeopleReadOnly={isPeopleReadOnly}
              />
            )}
          </>
        </Form>
      </FormCard>
      <CustomModal
        data-testid="large-attachments-modal"
        visible={confirmationForLargeAttachmentsModal}
        title={t('common:question')}
        subdescription={t('transaction:attachmentTooLargeForTransactionNote')}
        confirmText={t('common:yes')}
        cancelText={t('common:no')}
        icon={<MessageQuestion />}
        onConfirm={handleConfirmLargeAttachments}
        onClose={handleCancelLargeAttachments}
      />
      <CustomModal
        data-testid="file-size-warning-modal"
        visible={showFileSizeWarning}
        title={t('common:error')}
        subdescription={t('transaction:emailSizeExceedsLimit')}
        confirmText={t('common:ok')}
        icon={<LinearCloseCirlce />}
        onClose={closeFileSizeWarning}
        onConfirm={closeFileSizeWarning}
      />
      <CustomModal
        data-testid="discard-changes-modal"
        visible={showDiscardDialog}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        icon={<Slash />}
        onConfirm={handleDiscardConfirm}
        onClose={handleDiscardCancel}
      />
    </div>
  );
};

export default ManageMatterRequest;
